import React, { forwardRef, HTMLAttributes, ReactNode, useId } from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { RadioGroup as RadioGroupUI } from '@/components/ui/radio-group';
import Label, { LabelProps } from '../Label';
import { cn } from '@/src/utils';
import { Circle } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { typoColorData, typoTypeData } from '../Typo';

export interface RadioProps {
  optionList?: {
    label: ReactNode | string;
    value: string;
    optionProps?: Omit<
      React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
      'value'
    >;
    labelProps?: LabelProps;
    className?: string;
  }[];
  rootProps?: React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>;
  groupProps?: HTMLAttributes<HTMLDivElement>;
  labelTypoColor?: keyof typeof typoColorData;
  labelTypoType?: keyof typeof typoTypeData;
  typoClassName?: string;
}

const RadioGroup = forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioProps
>(
  (
    {
      optionList,
      labelTypoColor = 'gray7',
      labelTypoType = 'list16r',
      rootProps,
      groupProps,
      typoClassName,
    },
    ref,
  ) => {
    const id = useId();

    return (
      <RadioGroupUI ref={ref} {...rootProps}>
        <div
          {...groupProps}
          className={cn('flex items-start gap-4', groupProps?.className)}
        >
          {optionList?.map(
            ({ value, label, optionProps, labelProps, className }, idx) => {
              return (
                <div
                  className={twMerge('flex items-start gap-2', className)}
                  key={idx.toString()}
                >
                  <RadioGroupPrimitive.Item
                    value={value}
                    id={`${id}-${value}`}
                    className={cn(
                      'group peer aspect-square h-4 w-4 translate-y-[6px] rounded-full border border-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-gray4',
                      optionProps?.className,
                    )}
                    {...optionProps}
                  >
                    <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
                      <Circle
                        color="#5628F5"
                        className={cn(
                          'h-2.5 w-2.5 fill-primary group-disabled:fill-gray4 group-disabled:stroke-gray4',
                        )}
                      />
                    </RadioGroupPrimitive.Indicator>
                  </RadioGroupPrimitive.Item>

                  <Label
                    htmlFor={`${id}-${value}`}
                    className="peer-disabled:text-gray4"
                    typoType={labelTypoType}
                    typoColor={labelTypoColor}
                    typoClassName={typoClassName}
                    {...labelProps}
                  >
                    {label}
                  </Label>
                </div>
              );
            },
          )}
        </div>
      </RadioGroupUI>
    );
  },
);

export default RadioGroup;
